import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Briefcase, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authService } from '../api/services';
import toast from 'react-hot-toast';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

const Field = ({ label, type = 'text', value, onChange, placeholder, readOnly, hint }) => (
  <div>
    <label className="block text-xs font-medium text-forest-700 mb-1.5">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly}
      className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none focus:border-forest-500
        ${readOnly ? 'bg-cream-100 border-cream-200 text-forest-400 cursor-not-allowed' : 'bg-white border-cream-300 text-forest-900'}`} />
    {hint && <p className="text-xs text-forest-400 mt-1">{hint}</p>}
  </div>
);

const TagInput = ({ label, hint, tags, onAdd, onRemove, placeholder }) => {
  const [input, setInput] = useState('');
  const add = () => { const v = input.trim(); if (!v) return; onAdd(v); setInput(''); };
  return (
    <div>
      <label className="block text-xs font-medium text-forest-700 mb-1.5">{label}</label>
      {hint && <p className="text-xs text-forest-400 mb-2">{hint}</p>}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs bg-forest-50 text-forest-700 border border-forest-100 px-3 py-1 rounded-full">
              {tag}
              <button type="button" onClick={() => onRemove(i)} className="text-forest-300 hover:text-rust-500"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
        <button type="button" onClick={add}
          className="px-4 py-2 rounded-xl bg-forest-800 text-cream-50 text-xs font-medium hover:bg-forest-700 transition-colors">Add</button>
      </div>
    </div>
  );
};

const fieldLabels = {
  firstName: 'First Name', lastName: 'Last Name', email: 'Email Address',
  password: 'Password', businessName: 'Enterprise Name', businessType: 'Sector Type',
  city: 'City', bio: 'Bio', yearsOfExperience: 'Years of Experience',
  hourlyRate: 'Hourly Rate', specializations: 'Specializations', qualifications: 'Qualifications',
  cohortId: 'Cohort',
};

const ENTERPRISE_TYPES = [
  'Tour Operation', 'Travel Agency', 'Accommodation',
  'Eco & Nature Tourism', 'Culture', 'Adventure Tourism',
  'Food & Culinary Tourism', 'Tour Guiding', 'Destination Marketing',
  'Creative Arts', 'Tourism Content Creation',
  'Destination Development', 'Design Thinking', 'Other',
];

const RegisterPage = () => {
  const [role,    setRole]    = useState('client');
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState([]);
  const errorRef = useRef(null);
  const { login } = useAuth();
  const navigate  = useNavigate();

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '', phone: '',
    businessName: '', businessType: '', city: '', country: 'Ghana',
    bio: '', yearsOfExperience: '', hourlyRate: '', currency: 'GHS', timezone: 'Africa/Accra',
  });

  const [specializations, setSpecializations] = useState([]);
  const [qualifications,  setQualifications]  = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [cohortId, setCohortId] = useState('');
  // Students must choose a cohort before the rest of the form appears
  const cohortLocked = role === 'client' && cohorts.length > 0 && !cohortId;
  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    authService.getCohorts()
      .then(res => setCohorts(res.data?.data?.cohorts ?? []))
      .catch(() => setCohorts([]));
  }, []);

  useEffect(() => {
    if (errors.length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (role === 'expert') {
      const ce = [];
      if (!specializations.length) ce.push({ field: 'specializations', message: 'Please add at least one specialization (e.g. Heritage Tourism, Eco-Tourism)' });
      if (!qualifications.length)  ce.push({ field: 'qualifications',  message: 'Please add at least one qualification (e.g. MSc Tourism Management)' });
      if (ce.length) { setErrors(ce); return; }
    }
    if (role === 'client' && cohorts.length > 0 && !cohortId) {
      setErrors([{ field: 'cohortId', message: 'Please select your cohort before registering' }]);
      return;
    }
    setLoading(true);
    try {
      const payload = { firstName: form.firstName, lastName: form.lastName, email: form.email, password: form.password, phone: form.phone || undefined };
      let res;
      if (role === 'client') {
        Object.assign(payload, { businessName: form.businessName, businessType: form.businessType || 'Tour Operator', country: form.country, city: form.city, cohortId: cohortId || undefined });
        res = await authService.registerClient(payload);
      } else {
        Object.assign(payload, { bio: form.bio, yearsOfExperience: Number(form.yearsOfExperience), hourlyRate: 0, currency: 'GHS', timezone: form.timezone, levelScope: 'ALL_LEVELS', specializations, qualifications });
        res = await authService.registerExpert(payload);
      }
      const { accessToken, refreshToken, user } = res.data?.data ?? {};
      if (accessToken && user) {
        login(user, { accessToken, refreshToken });
        toast.success(`Welcome, ${user.firstName}! Account created.`);
        if (user.role === 'SUPER_ADMIN') navigate('/admin');
        else if (user.role === 'EXPERT') navigate('/expert/dashboard');
        else navigate('/dashboard');
      } else {
        toast.success('Account created! Please wait for admin approval before logging in.');
        navigate('/login');
      }
    } catch (err) {
      const se = err.response?.data?.errors;
      if (Array.isArray(se) && se.length) setErrors(se);
      else setErrors([{ field: 'general', message: err.response?.data?.message || 'Registration failed. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* Left — Video panel */}
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden flex-col justify-between p-12">
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" style={{ zIndex: 0 }}>
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        <div className="absolute inset-0"
          style={{ zIndex: 1, background: 'linear-gradient(160deg, rgba(4,16,8,0.55) 0%, rgba(6,22,12,0.50) 50%, rgba(3,12,6,0.60) 100%)' }} />
        <div className="absolute top-0 left-0 right-0 h-0.5" style={{ zIndex: 2, background: 'linear-gradient(90deg, #dc2626, #d97706, #16a34a, #d97706, #dc2626)' }} />

        <div className="relative flex flex-col justify-between h-full" style={{ zIndex: 2 }}>
          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-forest-900 text-xl">TourEnt</span>
              <span className="font-serif font-semibold text-forest-500 text-xl"> Hub</span>
            </div>
            <div className="hidden sm:block text-[9px] tracking-[0.18em] uppercase text-white font-sans border-l border-forest-300 pl-3 ml-1">
              Tourism · Expertise · Growth
            </div>
          </Link>

          <div className="space-y-4">
            <h2 className="text-3xl text-white leading-snug" style={{ fontFamily: 'Georgia, serif', fontWeight: 700 }}>
              Join The leading<br />
              <span className="italic font-light" style={{ color: '#86efac' }}>entrepreneurship</span><br />
              capacity building platform.
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Whether you are a budding entrepreneur, established business or an industry expert, Tourent Hub connects you with the right person.
            </p>
          </div>

          <div className="space-y-2.5">
            {[
              'Get matched with verified experts',
              'Book video, audio or chat sessions',
              'Access session recordings & transcripts',
              'Track your learning progress by level',
            ].map(f => (
              <p key={f} className="text-xs flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <span style={{ color: '#4ade80' }}>✓</span> {f}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Form panel */}
      <div className="w-full lg:w-7/12 flex items-start justify-center px-6 py-10 bg-cream-50 overflow-y-auto">
        <div className="w-full max-w-lg">

          <Link to="/" className="flex items-center gap-3 group mb-8">
            <div className="flex items-baseline gap-0.5">
              <span className="font-serif font-semibold text-xl text-black">TourEnt</span>
              <span className="font-serif font-semibold text-xl text-green-400"> Hub</span>
            </div>
            <div className="hidden sm:block text-[9px] tracking-[0.18em] uppercase font-sans border-l pl-3 ml-1 text-forest-400 border-forest-200">
              Tourism · Expertise · Growth
            </div>
          </Link>

          <h1 className="font-serif text-3xl text-forest-900 mb-1">Create your account</h1>
          <p className="text-sm text-forest-500 mb-8">Join the leading tourism expert marketplace.</p>

          {/* Role selector */}
          <div className="grid grid-cols-2 gap-3 mb-8">
            {[
              { id: 'client', icon: User,      label: 'Trainee',         sub: '' },
              { id: 'expert', icon: Briefcase, label: 'Resource Person', sub: '' },
            ].map(r => (
              <button key={r.id} type="button" onClick={() => { setRole(r.id); setErrors([]); }}
                className={`flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left
                  ${role === r.id ? 'border-forest-700 bg-forest-50' : 'border-cream-200 bg-white hover:border-forest-300'}`}>
                <r.icon size={18} className={role === r.id ? 'text-forest-700' : 'text-forest-400'} />
                <span className={`text-sm font-semibold mt-2 ${role === r.id ? 'text-forest-900' : 'text-forest-600'}`}>{r.label}</span>
                <span className="text-xs text-forest-400">{r.sub}</span>
              </button>
            ))}
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div ref={errorRef} className="bg-rust-50 border border-rust-200 rounded-xl px-4 py-4 mb-5 space-y-2">
              <div className="flex items-center gap-2 text-rust-700 font-medium text-sm">
                <AlertCircle size={15} className="flex-shrink-0" />
                Please fix the following before continuing:
              </div>
              <ul className="space-y-1 pl-5">
                {errors.map((err, i) => (
                  <li key={i} className="text-xs text-rust-600 list-disc">
                    {err.field && err.field !== 'general' && fieldLabels[err.field]
                      ? <><span className="font-semibold">{fieldLabels[err.field]}:</span> {err.message}</>
                      : err.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {role === 'client' && cohorts.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-forest-700 mb-1.5">Select your cohort *</label>
                <select value={cohortId} onChange={e => setCohortId(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
                  <option value="">Choose the cohort you belong to...</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <p className="text-xs text-forest-400 mt-1">You must pick a cohort before you can register.</p>
              </div>
            )}
            {cohortLocked ? (
              <div className="bg-forest-50 border border-forest-100 rounded-xl px-4 py-3">
                <p className="text-xs text-forest-600">Select your cohort above to continue with registration.</p>
              </div>
            ) : (<>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name" value={form.firstName} onChange={upd('firstName')} placeholder="John" />
              <Field label="Last Name"  value={form.lastName}  onChange={upd('lastName')}  placeholder="Mensah" />
            </div>
            <Field label="Email Address" type="email"    value={form.email}    onChange={upd('email')}    placeholder="you@example.com" />
            <Field label="Password"      type="password" value={form.password} onChange={upd('password')} placeholder="••••••••" hint="Minimum 8 characters" />
            <Field label="Phone (optional)" type="tel"   value={form.phone}    onChange={upd('phone')}    placeholder="+233244000001" />

            {role === 'client' && (
              <>
                <Field label="Enterprise Name" value={form.businessName} onChange={upd('businessName')} placeholder="Accra Tours Ltd" />
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Sector Type</label>
                  <select value={form.businessType} onChange={upd('businessType')}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white">
                    <option value="">Select type...</option>
                    {ENTERPRISE_TYPES.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="City"    value={form.city}    onChange={upd('city')}    placeholder="Accra" />
                  <Field label="Country" value={form.country} readOnly />
                </div>
              </>
            )}

            {role === 'expert' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-forest-700 mb-1.5">Bio <span className="text-forest-400 font-normal">(min. 50 characters)</span></label>
                  <textarea value={form.bio} onChange={upd('bio')} rows={4}
                    placeholder="Tell clients about your tourism expertise, background and what you can help them achieve..."
                    className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white resize-none" />
                </div>

                <Field label="Years of Experience" type="number" value={form.yearsOfExperience} onChange={upd('yearsOfExperience')} placeholder="5" />

                {/* Hourly Rate & Currency — disabled, set by admin */}
                <div className="grid grid-cols-2 gap-3 hidden">
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">Hourly Rate</label>
                    <input type="number" value="" placeholder="Set by admin" disabled
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-200 text-sm text-forest-400 bg-cream-50 cursor-not-allowed" />
                    <p className="text-xs text-forest-400 mt-1">Set by admin</p>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-forest-700 mb-1.5">Currency</label>
                    <select disabled
                      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-200 text-sm text-forest-400 bg-cream-50 cursor-not-allowed">
                      <option>GHS (GH₵)</option>
                    </select>
                    <p className="text-xs text-forest-400 mt-1">Set by admin</p>
                  </div>
                </div>

                <TagInput label="Specializations *" hint="Add areas you specialize in. Press Enter or click Add."
                  placeholder="e.g. Heritage Tourism, Eco-Tourism..."
                  tags={specializations}
                  onAdd={v => setSpecializations(s => [...s, v])}
                  onRemove={i => setSpecializations(s => s.filter((_, idx) => idx !== i))} />
                <TagInput label="Qualifications *" hint="Add your qualifications. Press Enter or click Add."
                  placeholder="e.g. MSc Tourism Management..."
                  tags={qualifications}
                  onAdd={v => setQualifications(s => [...s, v])}
                  onRemove={i => setQualifications(s => s.filter((_, idx) => idx !== i))} />
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                  <p className="text-xs text-amber-700">⚠️ Expert accounts require admin approval before you can log in. You'll be notified once reviewed.</p>
                </div>
              </>
            )}

            <button type="submit" disabled={loading}
              className="btn-rust w-full justify-center mt-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none">
              {loading
                ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating account...</>
                : 'Create Account'}
            </button>
            </>)}
          </form>

          <p className="text-center text-sm text-forest-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-forest-800 font-medium hover:underline">Sign in →</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;